import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, Check, AlertCircle } from "lucide-react";

const API = '/api';

const SOURCE_BADGE = {
  assignment: "bg-[#0F6CBD] text-white",
  brand_default: "bg-[#F3F2F1] text-[#5E5E5E]",
  tenant_default: "bg-[#E1DFDD] text-[#5E5E5E]",
};

export default function BrandAssignment() {
  const [effective, setEffective] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBrand, setEditingBrand] = useState(null);
  const [editFull, setEditFull] = useState("");
  const [editShort, setEditShort] = useState("");

  const fetchData = async () => {
    setLoading(true);
    const [eRes, tRes] = await Promise.all([
      axios.get(`${API}/assignments/effective`),
      axios.get(`${API}/templates/`),
    ]);
    setEffective(eRes.data.effective);
    setTemplates(tRes.data.templates);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const startEdit = (row) => {
    setEditingBrand(row.brand_id);
    setEditFull(row.full_template.id || "");
    setEditShort(row.short_template.id || "");
  };

  const saveAssignment = async (brandId) => {
    if (editFull) {
      await axios.post(`${API}/assignments/`, {
        brand_id: brandId,
        template_id: editFull,
        signature_mode: "full",
        is_default: true,
        active: true,
      });
    }
    if (editShort) {
      await axios.post(`${API}/assignments/`, {
        brand_id: brandId,
        template_id: editShort,
        signature_mode: "short",
        is_default: true,
        active: true,
      });
    }
    setEditingBrand(null);
    toast.success("Assignments saved");
    fetchData();
  };

  const fullTemplates = templates.filter((t) => t.mode === "FULL");
  const shortTemplates = templates.filter((t) => t.mode === "SHORT");

  return (
    <div className="p-8 max-w-[1600px]" data-testid="brand-assignment-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#171717]">Brand Assignment</h1>
          <p className="text-sm text-[#5E5E5E] mt-1">Assign default full and short templates per brand</p>
        </div>
        <Button onClick={fetchData} className="bg-[#F3F2F1] text-[#171717] hover:bg-[#E1DFDD] rounded-sm" data-testid="refresh-assignments-btn">
          <RefreshCw size={14} className="mr-2" /> Refresh
        </Button>
      </div>

      <div className="mt-2 flex items-center gap-4 text-[10px]">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#0F6CBD]" /> Explicit assignment</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#F3F2F1] border border-[#E1DFDD]" /> Brand default</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#E1DFDD]" /> Tenant fallback</span>
      </div>

      {loading ? (
        <p className="text-sm text-[#5E5E5E] mt-6">Loading assignments...</p>
      ) : (
        <div className="mt-6 bg-white border border-[#E1DFDD] rounded-sm overflow-hidden" data-testid="assignment-table">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="text-xs uppercase bg-[#FAFAFA] text-[#171717] border-b border-[#E1DFDD]">
                <th className="px-4 py-3 font-semibold">Brand</th>
                <th className="px-4 py-3 font-semibold">Full Template</th>
                <th className="px-4 py-3 font-semibold">Source</th>
                <th className="px-4 py-3 font-semibold">Short Template</th>
                <th className="px-4 py-3 font-semibold">Source</th>
                <th className="px-4 py-3 font-semibold w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {effective.map((row) => (
                <tr key={row.brand_id} className="border-b border-[#E1DFDD] hover:bg-[#FAFAFA]" data-testid={`brand-row-${row.brand_id}`}>
                  <td className="px-4 py-3 font-medium text-[#171717]">{row.brand_name}</td>
                  {editingBrand === row.brand_id ? (
                    <>
                      <td className="px-4 py-3" colSpan={2}>
                        <select value={editFull} onChange={(e) => setEditFull(e.target.value)} className="w-full border border-[#E1DFDD] rounded-sm px-2 py-1 text-xs" data-testid="edit-full-select">
                          <option value="">-- None --</option>
                          {fullTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3" colSpan={2}>
                        <select value={editShort} onChange={(e) => setEditShort(e.target.value)} className="w-full border border-[#E1DFDD] rounded-sm px-2 py-1 text-xs" data-testid="edit-short-select">
                          <option value="">-- None --</option>
                          {shortTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button size="sm" onClick={() => saveAssignment(row.brand_id)} className="bg-[#107C10] text-white hover:bg-[#0B6A0B] rounded-sm text-xs h-7" data-testid="save-assignment-btn">
                            <Check size={12} className="mr-1" /> Save
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingBrand(null)} className="border-[#E1DFDD] rounded-sm text-xs h-7">Cancel</Button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3 text-[#5E5E5E]">
                        {row.full_template.name}
                        {row.full_template.duplicate_defaults?.length > 0 && (
                          <div className="mt-1 text-[10px] text-[#A4262C] flex items-center gap-1" data-testid={`dupe-warning-full-${row.brand_id}`}>
                            <AlertCircle size={11} />
                            <span>{row.full_template.duplicate_defaults.length} active defaults — re-save below to clean up</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-semibold ${SOURCE_BADGE[row.full_template.source] || ""}`}>
                          {row.full_template.source?.replace("_", " ")}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[#5E5E5E]">
                        {row.short_template.name}
                        {row.short_template.duplicate_defaults?.length > 0 && (
                          <div className="mt-1 text-[10px] text-[#A4262C] flex items-center gap-1" data-testid={`dupe-warning-short-${row.brand_id}`}>
                            <AlertCircle size={11} />
                            <span>{row.short_template.duplicate_defaults.length} active defaults — re-save below to clean up</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-semibold ${SOURCE_BADGE[row.short_template.source] || ""}`}>
                          {row.short_template.source?.replace("_", " ")}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Button size="sm" variant="outline" onClick={() => startEdit(row)} className="border-[#E1DFDD] rounded-sm text-xs h-7" data-testid={`edit-brand-${row.brand_id}`}>
                          Edit
                        </Button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6 bg-[#EBF3FC] border border-[#0F6CBD]/20 rounded-sm p-4">
        <div className="flex items-start gap-2">
          <AlertCircle size={16} className="text-[#0F6CBD] mt-0.5 shrink-0" />
          <div className="text-xs text-[#5E5E5E]">
            <p className="font-medium text-[#171717]">Resolution priority</p>
            <p className="mt-1">Explicit assignment &gt; Brand default template &gt; Tenant default. Unassigned senders fall back to tenant defaults.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
